<template>
  <div class="level">
    <LevelRow
      @toggle="toggleVideo"
      :hasAccess="hasAccess || isVideoFree()"
      :contentType="contentType"
      :contentId="video.id"
      :status="videoStatus"
      :statusText="statusText"
      :courseStatus="courseStatus"
      :title="title"
      :plansLink="plansLink"
      :isWatching="isWatching"
      :details="getDuration()"
      :progress="progress"
      @click.native="toggleVideo"
    >
    </LevelRow>
    <div class="level__content" v-if="isWatching">
      <div class="content__videos-wrapper">
        <div class="content__video">
          <CourseVideo
            :video="video"
            :isWatching="isWatching"
            :quizId="quiz && quiz.id ? quiz.id : ''"
            :hasPassedQuiz="isQuizFinished(quizStatus)"
            :nextVideo="nextVideo"
            :videoStatus="videoStatus"
            :courseId="courseId"
            @is-loading-video="setLoader"
            @toggle-video="$emit('toggle-video')"
            @toggle-watched="$emit('toggle-watched')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isFreeContent } from '@/helpers'
import { mapState, mapGetters } from 'vuex'
import { intervalToDuration, addSeconds } from 'date-fns'
import CONSTANTS from '@/constants'
import LevelRow from './level/LevelRow.vue'
import CourseVideo from './level/CourseVideo.vue'

export default {
  name: 'VideoLevel',
  data() {
    return {
      isLoading: false,
    }
  },
  components: {
    LevelRow,
    CourseVideo,
  },
  props: {
    courseId: {
      type: String,
      required: true,
    },
    courseStatus: {
      type: String,
      required: true,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    nextVideo: {
      type: Object,
      required: true,
    },
    plansLink: {
      type: String,
      required: true,
    },
    quiz: {
      type: Object,
      required: false,
    },
    quizStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    video: {
      type: Object,
      required: true,
    },
    videoStatus: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState({
      playingVideoId: state => state.content.playingVideoId,
      currentHoveredNote: state => state.content.currentHoveredNote,
    }),
    ...mapGetters({
      courseBookmarks: 'content/courseBookmarks',
    }),
    isViewingLevelNote() {
      if (this.levelBookmarks && this.levelBookmarks.length) {
        const ids = this.levelBookmarks.map(bookmark => bookmark.video)
        return ids.includes(this.currentHoveredNote)
      }
      return false
    },
    isWatching() {
      return this.video.id === this.playingVideoId
    },
    levelBookmarks() {
      return this.courseBookmarks.filter(
        bookmark => bookmark.video === this.video.id
      )
    },
    contentType() {
      return CONSTANTS.CONTENT_TYPES.VIDEO
    },
    progress() {
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        return 50
      }
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        return 100
      }
      return 0
    },
    statusText() {
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        return `Resume video`
      }
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        return `Restart video`
      }
      return `Start video`
    },
  },
  methods: {
    async toggleVideo() {
      if (this.hasAccess && !this.isLoading) {
        this.isLoading = true
        const { playingVideoId, video } = this
        if (playingVideoId === video.id) {
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setPlayingVideoTime', 0)
        } else {
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setPlayingVideoId', video.id)
          this.$emit('toggle-content', CONSTANTS.CONTENT_TYPES.VIDEO)
        }
        this.isLoading = false
      }
    },
    toggleNextContent() {
      const { nextVideo } = this
      if (nextVideo) {
        this.$store.dispatch('content/setPlayingVideoId', nextVideo.uuid)
        this.$store.dispatch('content/setPlayingVideoTime', 0)
      }
    },
    isVideoFree() {
      return isFreeContent(this.video)
    },
    setLoader(state) {
      this.isLoading = state
    },
    isQuizFinished(status) {
      return CONSTANTS.CONTENT_USER_STATUS.FINISHED === status
    },
    getDuration() {
      const date = new Date()
      const duration = intervalToDuration({
        start: date,
        end: addSeconds(date, this.video.duration_in_seconds || 0),
      })

      return duration.hours > 0
        ? `${duration.hours}h ${duration.minutes}m ${duration.seconds}s`
        : `${duration.minutes}m ${duration.seconds}s`
    },
  },
}
</script>
